import { Container, Graphics } from 'pixi.js';
import { Symbol } from './Symbol.js';
import { gameConfig } from '../config/gameConfig.js';
import gsap from 'gsap';

/**
 * Cumulative extra strip cells for column index `col` so total stagger travel strictly
 * increases with each reel (avoids integer-round collisions when ideal cells per step is below 1).
 * @param {number} col 0-based column (reel 0 → 0)
 * @param {number} deltaIdeal cells per stagger step ≈ v×stagger/(rCruise×cellHeight)
 */
function cumulativeStaggerTravelCells(col, deltaIdeal) {
  if (col <= 0 || !(deltaIdeal > 0)) return 0;
  let prev = 0;
  for (let c = 1; c <= col; c++) {
    let t = Math.round(c * deltaIdeal);
    if (t <= prev) t = prev + 1;
    prev = t;
  }
  return prev;
}

export class Reel {
  /**
   * @param {{
   *   index: number,
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.index = opts.index;
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;

    this.root = new Container();
    this.maskShape = new Graphics();

    /** @type {Container} */
    this.stripContainer = new Container();
    this.root.addChild(this.stripContainer);
    this._applyMask();

    /** @type {number[]} */
    this._stripData = [];
    /** @type {Symbol[]} */
    this._stripSymbols = [];

    this._ensureStripPool(this._getRequiredStripLength());
    this._landIndex = this._getDefaultLandIndex();
    this._syncAllSymbolsFromData();
    this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + gameConfig.rowCount + 1);
    this._applyStripScrollPosition();
    /** @type {import('gsap').core.Timeline | null} */
    this._spinTimeline = null;
    /** @type {(() => void) | null} */
    this._spinResolve = null;
    /** @type {import('gsap').core.Tween | null} */
    this._pendingSpinTween = null;
    /** @type {import('gsap').core.Tween | null} */
    this._fastStopTween = null;
    /** Last strip index range kept active during spin (narrow viewport window); null when not using sliding activation. */
    this._spinActiveWindow = null;
    /** True while cruise-phase scroll is active (cleared at `land`, before deceleration). */
    this._scrollBlurActive = false;
    /**
     * Always true: cruise keeps Spine on `idle` only (Blazing Sevens). Scroll motion reads as blur.
     * Spine `spinning`/`blur` clips are never started during reel motion.
     */
    this._liteScrollBlur = true;
    /** Inclusive strip index range that previously received blur (unused — idle-only cruise). */
    this._scrollBlurLo = 0;
    this._scrollBlurHi = -1;
    /** Viewport pad for the current spin (idle-only cruise — small pad for all tiers). */
    this._spinViewportPad = Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 2);
    /** @type {{ colResult: number[], stopIndex: number, dir: number, cruisePxPerSec: number, fastStop: boolean } | null} */
    this._spinMotion = null;
    /** True after mid-spin anticipation has been applied this spin. */
    this._anticipationApplied = false;
    /** @type {{ endY: number, overshootY: number, approachDur: number, settleDur: number, useBounce: boolean } | null} */
    this._spinLandTail = null;
    /** @type {(() => void) | null} */
    this._spinSettleFn = null;
    /** @type {(() => void) | null} Per-reel land SFX/visuals — fire at impact, not after bounce settle. */
    this._onLanded = null;
    /** @type {(() => void) | null} After the strip is normalized onto the visible window. */
    this._onSettled = null;
    this._landedNotified = false;
  }

  /** Fire once when the reel hits the stop (overshoot peak / lock), not after bounce-out. */
  _notifyLanded() {
    if (this._landedNotified) return;
    this._landedNotified = true;
    const cb = this._onLanded;
    this._onLanded = null;
    if (typeof cb !== 'function') return;
    try {
      cb();
    } catch (_e) {
      /* never let SFX break the spin */
    }
  }

  _getSpinDirection() {
    return (gameConfig.reelSpinDirection ?? 1) < 0 ? -1 : 1;
  }

  _getMaxTravelCells() {
    const maxLanding =
      gameConfig.stripLandingOffsetMin + Math.max(0, gameConfig.stripLandingOffsetSpread - 1);
    const maxExtra =
      gameConfig.extraSpinCellsMin + Math.max(0, gameConfig.extraSpinCellsSpread - 1);
    const fx = gameConfig.visualFx ?? {};
    const anticMax =
      (fx.anticipationExtraCellsMin ?? 6) +
      Math.max(0, (fx.anticipationExtraCellsSpread ?? 4) - 1);
    const base = maxLanding + maxExtra + anticMax;
    const staggerExtra = this._getMaxStaggerExtraCells();
    return base + staggerExtra;
  }

  /**
   * Worst-case extra strip cells for right-most reel (stagger at constant cruise speed).
   */
  _getMaxStaggerExtraCells() {
    const ch = gameConfig.cellHeight;
    const ratios = gameConfig.spinDistanceRatios;
    const rCruise = ratios?.cruise ?? 0.7;
    if (rCruise <= 0 || ch <= 0) return 0;
    const lastCol = Math.max(0, gameConfig.reelCount - 1);
    const stagger = gameConfig.reelStopStaggerSec ?? 0;
    if (lastCol === 0 || stagger <= 0) return 0;
    const v = this._resolveCruisePxPerSec();
    const deltaIdeal = (v * stagger) / (rCruise * ch);
    return cumulativeStaggerTravelCells(lastCol, deltaIdeal) + 4;
  }

  /**
   * Shared cruise speed (px/s) — same for every reel and bet tier; only stagger spacing may differ.
   */
  _resolveCruisePxPerSec() {
    const ch = gameConfig.cellHeight;
    const ratios = gameConfig.spinDistanceRatios;
    const rCruise = ratios?.cruise ?? 0.7;
    const tCruise = Math.max(0.05, gameConfig.spinPhaseCruise || 0.26);
    const refCells =
      gameConfig.stripLandingOffsetMin +
      gameConfig.extraSpinCellsMin +
      Math.floor(
        (Math.max(0, gameConfig.stripLandingOffsetSpread - 1) +
          Math.max(0, gameConfig.extraSpinCellsSpread - 1)) /
          2,
      );
    const refCruisePx = refCells * ch * rCruise;
    const derived = refCruisePx / tCruise;
    const cap = Math.max(500, Number(gameConfig.reelCruiseDerivedCapPxPerSec) || 3400);
    const configured = gameConfig.reelCruisePixelsPerSecond;
    return configured != null && Number.isFinite(configured) && configured > 0
      ? configured
      : Math.min(derived, cap);
  }

  _getRequiredStripLength() {
    const baseForDownward = this._getMaxTravelCells() + 20 + gameConfig.rowCount;
    return Math.max(gameConfig.stripMinLength, baseForDownward + 8);
  }

  _getDefaultLandIndex() {
    if (this._getSpinDirection() > 0) return 10;
    return this._getMaxTravelCells() + 14;
  }

  _applyMask() {
    // Masking disabled: allow Spine animations to render fully without clipping.
    this.stripContainer.mask = null;
  }

  /**
   * Grow-only strip pool. Never destroy mid-session — create/destroy of Spine-backed
   * Symbols on every settle was thrashing old Android WebViews (especially on manual stop,
   * when all reels settle together). Inactive cells stay pooled and release Spines via setActive.
   */
  _ensureStripPool(length) {
    const { cellWidth: cw, cellHeight: ch } = gameConfig;
    while (this._stripData.length < length) {
      const i = this._stripData.length;
      this._stripData.push(this._getRandomSymbolId());
      const sym = new Symbol({
        spineAssets: this.spineAssets,
        animationController: this.animationController,
      });
      sym.setSymbolId(this._stripData[i]);
      sym.root.x = cw / 2;
      sym.root.y = i * ch + ch / 2;
      this.stripContainer.addChild(sym.root);
      this._stripSymbols.push(sym);
    }
  }

  /**
   * Randomize strip cells outside the landing window (filler while spinning).
   * @param {number} landIndex First index of the result window (protected).
   * @param {number} rowCount Rows in the result window.
   */
  _randomizeStripOutsideWindow(landIndex, rowCount) {
    for (let i = 0; i < this._stripData.length; i++) {
      if (i >= landIndex && i < landIndex + rowCount) continue;
      this._stripData[i] = this._getRandomSymbolId();
    }
  }

  _getRandomSymbolId() {
    const symbols = gameConfig.symbols;
    if (!Array.isArray(symbols) || symbols.length === 0) return 0;
    const idx = Math.floor(Math.random() * symbols.length);
    return symbols[idx]?.id ?? 0;
  }

  _syncAllSymbolsFromData() {
    for (let i = 0; i < this._stripSymbols.length; i++) {
      this._stripSymbols[i].setSymbolId(this._stripData[i]);
    }
  }

  _setActiveSymbolRange(fromIndex, toIndex) {
    const lo = Math.max(0, fromIndex);
    const hi = Math.min(this._stripSymbols.length - 1, toIndex);
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const shouldBeActive = i >= lo && i <= hi;
      if (shouldBeActive) {
        // Unconditionally clear any spin state before activating — blur must never persist.
        this._stripSymbols[i].clearSpinPresentation();
      }
      this._stripSymbols[i].setActive(shouldBeActive);
    }
    this._spinActiveWindow = null;
  }

  _resetSpinActiveWindowState() {
    this._spinActiveWindow = null;
  }

  /**
   * Activates only strip cells near the viewport for the current `stripContainer.y`.
   * Keeps the same scroll motion while avoiding dozens of simultaneous Spine updates off-screen.
   * @param {number} ch cell height
   * @param {number} rowCount visible rows
   * @param {number} pad extra strip indices above/below the window
   */
  _updateStripActiveFromScroll(ch, rowCount, pad) {
    const n = this._stripSymbols.length;
    if (n <= 0 || ch <= 0) return;

    const vy = -this.stripContainer.y / ch;
    const lo = Math.floor(vy) - pad;
    const hi = Math.ceil(vy + rowCount) - 1 + pad;
    const loC = Math.max(0, lo);
    const hiC = Math.min(n - 1, hi);

    const prev = this._spinActiveWindow;
    if (prev && prev.lo === loC && prev.hi === hiC) return;

    if (!prev) {
      for (let i = 0; i < n; i++) {
        const on = i >= loC && i <= hiC;
        this._stripSymbols[i].setSymbolId(this._stripData[i]);
        this._stripSymbols[i].setActive(on);
      }
      this._spinActiveWindow = { lo: loC, hi: hiC };
      this._syncScrollBlurForActiveWindow(loC, hiC);
      return;
    }

    for (let i = prev.lo; i <= prev.hi; i++) {
      if (i < loC || i > hiC) {
        this._stripSymbols[i].setActive(false);
      }
    }
    for (let i = loC; i <= hiC; i++) {
      if (i < prev.lo || i > prev.hi) {
        this._stripSymbols[i].setSymbolId(this._stripData[i]);
        this._stripSymbols[i].setActive(true);
      }
    }
    this._spinActiveWindow = { lo: loC, hi: hiC };
    this._syncScrollBlurForActiveWindow(loC, hiC);
  }

  /** Apply _stripData to sprites in a range except a window. */
  _syncStripSymbolsInRange(skipFrom, skipRowCount, fromIndex = 0, toIndex = this._stripSymbols.length - 1) {
    const lo = Math.max(0, fromIndex);
    const hi = Math.min(this._stripSymbols.length - 1, toIndex);
    for (let i = lo; i <= hi; i++) {
      if (i >= skipFrom && i < skipFrom + skipRowCount) continue;
      this._stripSymbols[i].setSymbolId(this._stripData[i]);
    }
  }

  _applyStripScrollPosition() {
    const ch = gameConfig.cellHeight;
    this.stripContainer.y = -this._landIndex * ch;
  }

  /**
   * Idle-only: never start Spine spinning/blur. When `spinning` is false, clear flags in range.
   */
  _setStripIndexRangeSpinning(startIdx, endIdx, spinning) {
    if (spinning) return;
    const n = this._stripSymbols.length;
    const lo = Math.max(0, Math.min(startIdx, endIdx));
    const hi = Math.min(n - 1, Math.max(startIdx, endIdx));
    for (let i = lo; i <= hi; i++) {
      this._stripSymbols[i].clearSpinPresentation();
    }
  }

  /** Clear spinning presentation on the whole strip (after a spin or cancel). */
  _setAllStripSymbolsSpinning(spinning) {
    if (spinning) return;
    for (let i = 0; i < this._stripSymbols.length; i++) {
      this._stripSymbols[i].clearSpinPresentation();
    }
  }

  _setVisibleWindowSpinning(spinning) {
    this._setStripIndexRangeSpinning(this._landIndex, this._landIndex + gameConfig.rowCount - 1, spinning);
  }

  _enableScrollBlur(_startIdx, _endIdx) {
    // Idle-only cruise — never start Spine spinning/blur clips (Blazing Sevens).
    this._scrollBlurActive = false;
    this._scrollBlurLo = 0;
    this._scrollBlurHi = -1;
  }

  _clearScrollBlur() {
    this._scrollBlurActive = false;
    this._scrollBlurLo = 0;
    this._scrollBlurHi = -1;
    // Only clear the active viewport — full-strip walks freeze Android when 5 reels settle together.
    if (this._spinActiveWindow) {
      const { lo, hi } = this._spinActiveWindow;
      for (let i = lo; i <= hi; i++) {
        this._stripSymbols[i]?.clearSpinPresentation();
      }
      return;
    }
    this._setAllStripSymbolsSpinning(false);
  }

  /**
   * Called after every viewport shift. Idle-only cruise: clear any leftover spinning flags;
   * never start Spine `spinning`/`blur` clips.
   */
  _syncScrollBlurForActiveWindow(loC, hiC) {
    for (let i = loC; i <= hiC; i++) {
      if (this._stripSymbols[i].isSpinning()) {
        this._stripSymbols[i].clearSpinPresentation();
      }
    }
  }

  _resolveSpinViewportPad(_turboMode) {
    const pad = Number(gameConfig.reelSpinViewportPadCells);
    if (Number.isFinite(pad) && pad >= 1) return Math.floor(pad);
    const turboPad = Number(gameConfig.turboReelSpinViewportPadCells);
    if (Number.isFinite(turboPad) && turboPad >= 1) return Math.floor(turboPad);
    return 2;
  }

  _stopPendingSpin() {
    if (this._pendingSpinTween) {
      const tw = this._pendingSpinTween;
      this._pendingSpinTween = null;
      tw.kill();
      this.animationController.releaseTween(tw);
    }
    this._applyStripScrollPosition();
    this._setVisibleWindowSpinning(false);
  }

  _clearFastStopTween() {
    if (!this._fastStopTween) return;
    const tw = this._fastStopTween;
    this._fastStopTween = null;
    tw.eventCallback('onComplete', null);
    tw.eventCallback('onInterrupt', null);
    tw.kill();
    this.animationController.releaseTween(tw);
  }

  /**
   * Play deceleration + bounce on the strip directly (reliable after turbo seek).
   * @param {import('gsap').core.Timeline} tl
   * @param {number} [timeScale]
   */
  _playLandingTail(tl, timeScale = 1) {
    const tail = this._spinLandTail;
    const settle = this._spinSettleFn;
    const scale = Number.isFinite(timeScale) && timeScale > 0 ? timeScale : 1;
    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const scrollPad = this._spinViewportPad || Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 2);
    const onStripY = () => {
      this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
    };

    this._clearFastStopTween();
    if (tl) {
      tl.eventCallback('onInterrupt', null);
      tl.eventCallback('onComplete', null);
      this._spinTimeline = null;
      tl.kill();
      this.animationController.releaseTween(tl);
    }

    if (!tail || typeof settle !== 'function') {
      return;
    }

    const approachDur = Math.max(0.012, tail.approachDur / scale);
    const settleDur = Math.max(0, tail.settleDur / scale);
    const bounceTl = this.animationController.createTimeline({
      onComplete: () => {
        this._clearFastStopTween();
        const fn = this._spinSettleFn;
        this._spinSettleFn = null;
        fn?.();
      },
    });
    this._fastStopTween = bounceTl;

    if (tail.useBounce && settleDur > 0.001) {
      bounceTl
        .to(this.stripContainer, {
          y: tail.overshootY,
          duration: approachDur,
          ease: 'power3.out',
          onUpdate: onStripY,
        })
        .call(() => this._notifyLanded())
        .to(this.stripContainer, {
          y: tail.endY,
          duration: settleDur,
          ease: 'power2.out',
          onUpdate: onStripY,
        });
    } else {
      bounceTl.to(this.stripContainer, {
        y: tail.endY,
        duration: approachDur,
        ease: 'power3.out',
        onUpdate: onStripY,
      });
    }
  }

  /**
   * @param {{ instant?: boolean, landTimeScale?: number }} [opts]
   *   `instant` — snap with no decel/bounce (cleanup only). Turbo/hand-stop use
   *   `landTimeScale` to play the same overshoot landing as tier 1, sped up.
   */
  requestStop(opts = {}) {
    const instant = !!opts.instant;
    const landTimeScale = opts.landTimeScale;
    if (!this._spinTimeline) return;
    this._clearFastStopTween();
    const tl = this._spinTimeline;
    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const scrollPad = this._spinViewportPad || Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 2);
    /** Skip remaining cruise — same landing tweens as a natural stop (no speed changes). */
    const tLand = tl.labels?.land;
    if (typeof tLand === 'number' && tl.duration() > 0) {
      if (tl.time() + 1e-6 < tLand) {
        // suppressEvents: seeking across cruise must NOT fire onUpdate for every
        // intermediate y — that would activate/release Spines for each sample and
        // freeze old Android devices when all 5 reels stop together.
        tl.time(tLand, true);
      }
      this._clearScrollBlur();
      this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
      if (instant) {
        tl.kill();
      } else {
        this._playLandingTail(tl, landTimeScale);
      }
      return;
    }
    if (instant) {
      tl.kill();
      return;
    }
    this._playLandingTail(tl, landTimeScale);
    this._clearScrollBlur();
    this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
  }

  /**
   * Kill an orphaned spin timeline without settling to a backend grid (new-round cleanup).
   */
  cancelActiveSpinAnimation() {
    const resolve = this._spinResolve;
    this._spinResolve = null;
    this._spinLandTail = null;
    this._spinSettleFn = null;
    this._onLanded = null;
    this._onSettled = null;
    this._landedNotified = true;
    this._spinMotion = null;
    this._anticipationApplied = false;
    this._clearFastStopTween();
    this._stopPendingSpin();
    if (this._spinTimeline) {
      const tl = this._spinTimeline;
      this._spinTimeline = null;
      tl.eventCallback('onComplete', null);
      tl.eventCallback('onInterrupt', null);
      tl.kill();
      this.animationController.releaseTween(tl);
    }
    this._clearScrollBlur();
    for (const sym of this._stripSymbols) {
      sym.clearSpinPresentation();
    }
    this._spinActiveWindow = null;
    resolve?.();
  }

  beginPendingSpin() {
    // Pre-spin stays idle — no Spine spinning/blur clips (Blazing Sevens idle cruise).
  }

  /**
   * After a spin, fold the visible triple back to a small land index to avoid drift.
   * @param {number[]} visibleTriple length rowCount (backend result for this reel)
   * @param {number | null} landedAtStripIndex strip index that was in the viewport when the spin
   *   tween finished (before scroll snap). If set, those cells keep `visibleTriple` in `_stripData`
   *   until after sync so we never paint random filler on the symbols the player still sees.
   */
  _normalizeStrip(visibleTriple, landedAtStripIndex = null) {
    const L = this._getRequiredStripLength();
    const base = this._getDefaultLandIndex();
    const rowCount = gameConfig.rowCount;
    // Grow if needed; never shrink/destroy — pool stays stable for the session.
    this._ensureStripPool(Math.max(L, this._stripData.length));
    for (let r = 0; r < rowCount; r++) {
      this._stripData[base + r] = visibleTriple[r];
    }
    if (
      landedAtStripIndex != null &&
      Number.isFinite(landedAtStripIndex) &&
      landedAtStripIndex >= 0 &&
      landedAtStripIndex + rowCount <= this._stripData.length
    ) {
      for (let r = 0; r < rowCount; r++) {
        this._stripData[landedAtStripIndex + r] = visibleTriple[r];
      }
    }
    this._landIndex = base;
    // Only sync the landing window + pad — full-strip setSymbolId on every settle
    // was unnecessary work (inactive cells pick up ids when activated).
    const pad = Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 2);
    const syncLo = Math.max(0, base - pad);
    const syncHi = Math.min(this._stripSymbols.length - 1, base + rowCount - 1 + pad);
    for (let i = syncLo; i <= syncHi; i++) {
      this._stripSymbols[i].setSymbolId(this._stripData[i]);
    }
    const ch = gameConfig.cellHeight;
    for (let i = 0; i < this._stripSymbols.length; i++) {
      this._stripSymbols[i].root.y = i * ch + ch / 2;
    }
    this._applyStripScrollPosition();
  }

  /**
   * @param {number[]} colResult length rowCount top→bottom symbol ids
   * @param {{ columnIndex?: number, fastStop?: boolean, turboMode?: boolean, liteScrollBlur?: boolean, reelStopStaggerScale?: number, travelBase?: { landingOffset: number, extraCells: number }, onLanded?: () => void }} [options]
   *   column 0 = left; stagger adds extra strip travel so each column stops later — same px/s for all.
   *   Pass the same `travelBase` from {@link ReelContainer#spinToGrid} for every reel so random travel matches.
   *   `reelStopStaggerScale` tightens stop spacing only (e.g. tier 2). `fastStop` snaps (legacy).
   *   `turboMode` uses a dedicated short travel + micro-stagger (Quick Spin) with full bounce landing.
   *   Idle-only cruise always (Blazing Sevens) — `liteScrollBlur` is ignored / always on.
   *   `onLanded` fires at stop impact (overshoot peak), not after bounce settle — keeps SFX on the beat.
   * @returns {Promise<void>}
   */
  spinToResult(colResult, options = {}) {
    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const col = Math.max(0, Number(options.columnIndex) || 0);
    const turboMode = !!options.turboMode;
    // Always idle-only during cruise — never play Spine spinning/blur clips.
    this._liteScrollBlur = true;
    this._spinViewportPad = this._resolveSpinViewportPad(turboMode);
    const staggerScaleRaw = options.reelStopStaggerScale;
    const staggerScale =
      staggerScaleRaw != null && Number.isFinite(staggerScaleRaw) && staggerScaleRaw >= 0
        ? staggerScaleRaw
        : 1;
    const staggerSec = turboMode
      ? Math.max(0, Number(gameConfig.turboReelStopStaggerSec) || 0.028)
      : (gameConfig.reelStopStaggerSec ?? 0) * staggerScale;
    const cruisePxPerSec = this._resolveCruisePxPerSec();

    this._clearFastStopTween();
    this._stopPendingSpin();
    this._resetSpinActiveWindowState();
    this._clearScrollBlur();
    if (this._spinTimeline) {
      const prevTl = this._spinTimeline;
      this._spinTimeline = null;
      prevTl.kill();
      this.animationController.releaseTween(prevTl);
    }
    this._applyStripScrollPosition();
    this._onLanded = typeof options.onLanded === 'function' ? options.onLanded : null;
    this._onSettled = typeof options.onSettled === 'function' ? options.onSettled : null;
    this._landedNotified = false;
    const startIndex = this._landIndex;

    /** What the player actually sees (ground truth — may differ from _stripData if ever out of sync). */
    const visibleSnap = [];
    for (let r = 0; r < rowCount; r++) {
      visibleSnap.push(this._stripSymbols[startIndex + r].getSymbolId());
    }

    const tb = options.travelBase;
    let landingOffset;
    let extraCells;
    if (
      tb &&
      Number.isFinite(tb.landingOffset) &&
      Number.isFinite(tb.extraCells)
    ) {
      landingOffset = tb.landingOffset;
      extraCells = tb.extraCells;
    } else if (turboMode) {
      landingOffset =
        (gameConfig.turboStripLandingOffsetMin ?? 4) +
        Math.floor(Math.random() * Math.max(1, gameConfig.turboStripLandingOffsetSpread ?? 2));
      extraCells =
        (gameConfig.turboExtraSpinCellsMin ?? 3) +
        Math.floor(Math.random() * Math.max(1, gameConfig.turboExtraSpinCellsSpread ?? 2));
    } else {
      landingOffset =
        gameConfig.stripLandingOffsetMin +
        Math.floor(Math.random() * gameConfig.stripLandingOffsetSpread);
      extraCells =
        gameConfig.extraSpinCellsMin + Math.floor(Math.random() * gameConfig.extraSpinCellsSpread);
    }
    let travel = landingOffset + extraCells;
    const anticipationExtra = Math.max(0, Number(options.anticipationExtraCells) || 0);
    travel += anticipationExtra;
    const ratiosEarly = gameConfig.spinDistanceRatios;
    const rCruiseEarly = ratiosEarly?.cruise ?? 0.7;
    if (!options.fastStop && staggerSec > 0 && col > 0 && rCruiseEarly > 0) {
      const deltaIdeal = (cruisePxPerSec * staggerSec) / (rCruiseEarly * ch);
      travel += cumulativeStaggerTravelCells(col, deltaIdeal);
    }
    const dir = this._getSpinDirection();
    let stopIndex = startIndex + dir * travel;
    if (stopIndex < 0) stopIndex = 0;

    if (stopIndex + rowCount >= this._stripData.length) {
      this._ensureStripPool(stopIndex + rowCount + 8);
    }

    for (let r = 0; r < rowCount; r++) {
      this._stripData[stopIndex + r] = colResult[r];
    }
    for (let r = 0; r < rowCount; r++) {
      this._stripData[startIndex + r] = visibleSnap[r];
    }
    this._syncStripSymbolsInRange(startIndex, rowCount, stopIndex, stopIndex + rowCount - 1);

    this._anticipationApplied = false;
    this._spinMotion = {
      colResult: colResult.slice(),
      stopIndex,
      dir,
      cruisePxPerSec,
      fastStop: !!options.fastStop,
      turboMode,
    };

    const startY = -startIndex * ch;
    const endY = -stopIndex * ch;
    this.stripContainer.y = startY;

    const scrollPad = this._spinViewportPad;
    this._updateStripActiveFromScroll(ch, rowCount, scrollPad);

    const ratios = gameConfig.spinDistanceRatios;
    const total = Math.abs(endY - startY);
    const d1 = total * ratios.accelerate;
    const d2 = total * ratios.cruise;
    const d3 = total * ratios.decelerate;
    const yDir = Math.sign(endY - startY) || -1;
    const y1 = startY + yDir * d1;
    const y2 = y1 + yDir * d2;

    const t1 = gameConfig.spinPhaseAccelerate;
    const t2 = gameConfig.spinPhaseCruise;
    const t3 = gameConfig.spinPhaseDecelerate;

    return new Promise((resolve) => {
      let settled = false;
      this._spinResolve = resolve;
      const settle = () => {
        if (settled) return;
        settled = true;
        this._spinResolve = null;
        this._spinLandTail = null;
        this._spinSettleFn = null;
        const landAt = this._spinMotion?.stopIndex ?? stopIndex;
        this._spinMotion = null;
        this._anticipationApplied = false;
        const tl = this._spinTimeline;
        this._spinTimeline = null;
        if (tl) this.animationController.releaseTween(tl);
        this._clearFastStopTween();
        // Keep strip pool length stable — never shrink/destroy Symbols here.
        // (each destroy/create of Spine on the shared ticker → severe lag after a few plays).
        this._clearScrollBlur();
        this._normalizeStrip(colResult, landAt);
        this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + rowCount + 1);
        // Only idle the visible pad — full-strip clear × 5 reels freezes mid/low Android.
        const idleLo = Math.max(0, this._landIndex - 1);
        const idleHi = Math.min(
          this._stripSymbols.length - 1,
          this._landIndex + rowCount + 1,
        );
        for (let i = idleLo; i <= idleHi; i++) {
          this._stripSymbols[i].clearSpinPresentation();
        }
        this._liteScrollBlur = true;

        /* Fallback if bounce path was skipped (fastStop / interrupt / no bounce). */
        this._notifyLanded();
        const onSettled = this._onSettled;
        this._onSettled = null;
        try {
          onSettled?.();
        } catch (_e) {
          /* never let badge/SFX break the spin */
        }
        resolve();
      };
      this._spinSettleFn = settle;

      this._spinTimeline = this.animationController.createTimeline({
        onComplete: settle,
        onInterrupt: settle,
      });
      if (options.fastStop) {
        this._spinTimeline.timeScale(38);
      }

      const s1 = t1;
      const s3 = t3;
      const cruiseDist = Math.abs(y2 - y1);
      const cruiseSlow =
        options.anticipated && !options.fastStop && !turboMode
          ? Math.max(1, Number(gameConfig.visualFx?.anticipationCruiseSlowFactor) || 1.55)
          : 1;
      const cruiseDur =
        cruisePxPerSec > 1 && cruiseDist > 0
          ? (cruiseDist / cruisePxPerSec) * cruiseSlow
          : t2 * cruiseSlow;

      const bounceRatio = Math.max(0, Number(gameConfig.reelLandBounceRatio) || 0);
      const bounceOutSec = Math.max(0, Number(gameConfig.reelLandBounceOut) || 0);
      const bounceInSec = Math.max(0, Number(gameConfig.reelLandBounceIn) || 0);
      // Turbo keeps the classic overshoot bounce (premium feel); only legacy fastStop skips it.
      const useLandBounce =
        !options.fastStop && bounceRatio > 0 && bounceOutSec > 0 && s3 > 0.012;
      const bouncePx = bounceRatio * ch;
      /** Past the lock position in the direction of travel, then snap back (classic slot landing). */
      const overshootY = endY + yDir * bouncePx;
      let settleDur = 0;
      let approachDur = s3;
      if (useLandBounce) {
        settleDur = Math.min(bounceOutSec, s3 * 0.5);
        approachDur = s3 - settleDur;
        const minApproach = bounceInSec > 0 ? bounceInSec : 0;
        if (minApproach > 0 && approachDur < minApproach) {
          approachDur = Math.min(minApproach, s3 * 0.88);
          settleDur = Math.max(0.02, s3 - approachDur);
        }
        if (turboMode) {
          const landScale = Math.max(1, Number(gameConfig.turboLandTimeScale) || 1.85);
          approachDur = Math.max(0.03, approachDur / landScale);
          settleDur = Math.max(0.02, settleDur / landScale);
        }
      }
      this._spinLandTail = {
        endY,
        overshootY,
        approachDur,
        settleDur,
        useBounce: useLandBounce,
      };

      const onStripY = () => {
        this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
      };
      if (s1 > 0.001) {
        this._spinTimeline.to(this.stripContainer, {
          y: y1,
          duration: s1,
          ease: 'power2.in',
          onUpdate: onStripY,
        });
      }
      this._spinTimeline.to(this.stripContainer, {
        y: y2,
        duration: cruiseDur,
        ease: 'none',
        onUpdate: onStripY,
      });
      this._spinTimeline.addLabel('land');
      // End of cruise — ensure idle (no leftover spin flags) before deceleration.
      this._spinTimeline.call(() => this._clearScrollBlur(), null, 'land');

      if (useLandBounce) {
        this._spinTimeline
          .to(this.stripContainer, {
            y: overshootY,
            duration: approachDur,
            ease: 'power3.out',
            onUpdate: onStripY,
          })
          .call(() => this._notifyLanded())
          .to(this.stripContainer, {
            y: endY,
            duration: settleDur,
            ease: 'power2.out',
            onUpdate: onStripY,
          });
      } else {
        this._spinTimeline.to(this.stripContainer, {
          y: endY,
          duration: s3,
          ease: 'power3.out',
          onUpdate: onStripY,
        });
      }
    });
  }

  /**
   * Mid-spin scatter anticipation: extend travel and slow remaining cruise.
   * Only applies while still in cruise (before the `land` label) — never spoils from spin start.
   * @param {{ extraCells?: number, slowFactor?: number }} [opts]
   * @returns {boolean}
   */
  applyAnticipationTease(opts = {}) {
    if (this._anticipationApplied) return false;
    if (!this._spinTimeline || !this._spinMotion) return false;
    if (this._spinMotion.fastStop) return false;

    const tl = this._spinTimeline;
    const tLand = tl.labels?.land;
    if (typeof tLand !== 'number' || tl.time() + 1e-6 >= tLand) return false;

    this._anticipationApplied = true;

    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const scrollPad = Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 2);
    const extra = Math.max(0, Math.floor(Number(opts.extraCells) || 0));
    const slow = Math.max(1, Number(opts.slowFactor) || 1);
    const motion = this._spinMotion;
    const dir = motion.dir;
    const colResult = motion.colResult;
    const cruisePxPerSec = motion.cruisePxPerSec;

    let newStopIndex = motion.stopIndex;
    if (extra > 0) {
      newStopIndex = motion.stopIndex + dir * extra;
      if (newStopIndex < 0) newStopIndex = 0;
      if (newStopIndex + rowCount >= this._stripData.length) {
        this._ensureStripPool(newStopIndex + rowCount + 8);
      }
      for (let r = 0; r < rowCount; r++) {
        this._stripData[newStopIndex + r] = colResult[r];
        this._stripSymbols[newStopIndex + r]?.setSymbolId(colResult[r]);
      }
      if (this._scrollBlurActive) {
        this._scrollBlurHi = Math.max(this._scrollBlurHi, newStopIndex + rowCount - 1);
        this._scrollBlurLo = Math.min(this._scrollBlurLo, newStopIndex);
      }
      motion.stopIndex = newStopIndex;
    }

    const currentY = this.stripContainer.y;
    const newEndY = -newStopIndex * ch;
    const remaining = Math.abs(newEndY - currentY);

    const ratios = gameConfig.spinDistanceRatios;
    const cruiseR = ratios?.cruise ?? 0.7;
    const decelR = ratios?.decelerate ?? 0.2;
    const sum = cruiseR + decelR;
    const d3 = remaining < 1 ? 0 : sum > 0 ? remaining * (decelR / sum) : remaining * 0.25;
    const dCruise = Math.max(0, remaining - d3);
    const yDir = Math.sign(newEndY - currentY) || motion.dir || -1;
    const y2 = currentY + yDir * dCruise;

    const s3 = gameConfig.spinPhaseDecelerate;
    const t2 = gameConfig.spinPhaseCruise;
    const cruiseDur =
      remaining < 1
        ? 0
        : cruisePxPerSec > 1 && dCruise > 0
          ? (dCruise / cruisePxPerSec) * slow
          : t2 * slow;

    const bounceRatio = Math.max(0, Number(gameConfig.reelLandBounceRatio) || 0);
    const bounceOutSec = Math.max(0, Number(gameConfig.reelLandBounceOut) || 0);
    const bounceInSec = Math.max(0, Number(gameConfig.reelLandBounceIn) || 0);
    const useLandBounce = bounceRatio > 0 && bounceOutSec > 0 && s3 > 0.012;
    const bouncePx = bounceRatio * ch;
    const overshootY = newEndY + yDir * bouncePx;
    let settleDur = 0;
    let approachDur = s3;
    if (useLandBounce) {
      settleDur = Math.min(bounceOutSec, s3 * 0.5);
      approachDur = s3 - settleDur;
      const minApproach = bounceInSec > 0 ? bounceInSec : 0;
      if (minApproach > 0 && approachDur < minApproach) {
        approachDur = Math.min(minApproach, s3 * 0.88);
        settleDur = Math.max(0.02, s3 - approachDur);
      }
    }
    this._spinLandTail = {
      endY: newEndY,
      overshootY,
      approachDur,
      settleDur,
      useBounce: useLandBounce,
    };

    const settle = this._spinSettleFn;
    tl.eventCallback('onComplete', null);
    tl.eventCallback('onInterrupt', null);
    this._spinTimeline = null;
    tl.kill();
    this.animationController.releaseTween(tl);

    const onStripY = () => {
      this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
    };

    this._spinTimeline = this.animationController.createTimeline({
      onComplete: settle,
      onInterrupt: settle,
    });
    if (cruiseDur > 0.001) {
      this._spinTimeline.to(this.stripContainer, {
        y: y2,
        duration: cruiseDur,
        ease: 'none',
        onUpdate: onStripY,
      });
    }
    this._spinTimeline.addLabel('land');
    this._spinTimeline.call(() => this._clearScrollBlur(), null, 'land');

    if (useLandBounce) {
      this._spinTimeline
        .to(this.stripContainer, {
          y: overshootY,
          duration: approachDur,
          ease: 'power3.out',
          onUpdate: onStripY,
        })
        .call(() => this._notifyLanded())
        .to(this.stripContainer, {
          y: newEndY,
          duration: settleDur,
          ease: 'power2.out',
          onUpdate: onStripY,
        });
    } else {
      this._spinTimeline.to(this.stripContainer, {
        y: newEndY,
        duration: Math.max(0.02, s3),
        ease: 'power3.out',
        onUpdate: onStripY,
      });
    }
    return true;
  }

  /**
   * Apply result symbols without spin animation.
   * @param {number[]} colResult length rowCount top→bottom symbol ids
   */
  setResultInstant(colResult) {
    this._stopPendingSpin();
    if (this._spinTimeline) {
      const prevTl = this._spinTimeline;
      this._spinTimeline = null;
      prevTl.kill();
      this.animationController.releaseTween(prevTl);
    }
    this._clearScrollBlur();
    this._normalizeStrip(colResult);
    this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + gameConfig.rowCount + 1);
  }

  /**
   * @param {number} row 0 top .. rowCount-1 bottom
   * @returns {Symbol}
   */
  getSymbolAtVisibleRow(row) {
    return this._stripSymbols[this._landIndex + row];
  }

  /**
   * Symbol in the window at the moment of landing. During the bounce, that is the
   * stop-index cell the player sees; after settle, the normalized visible row.
   * @param {number} row
   * @returns {Symbol | undefined}
   */
  getLandingSymbol(row) {
    const stopIndex = this._spinMotion?.stopIndex;
    const index = Number.isFinite(stopIndex) ? stopIndex : this._landIndex;
    return this._stripSymbols[index + row];
  }

  /**
   * Replace one currently visible symbol (0=top..2=bottom) with backend symbol id.
   * @param {number} row
   * @param {number} symbolId
   */
  setVisibleRowSymbolId(row, symbolId) {
    const sym = this.getSymbolAtVisibleRow(row);
    if (!sym) return;
    sym.setSymbolId(symbolId);
  }

  /** Every visible cell: active Spine, idle clip, full alpha (no dim / noWin / spin blur). */
  showVisibleWindowSharp() {
    this._clearScrollBlur();
    const lo = Math.max(0, this._landIndex - 1);
    const hi = Math.min(
      this._stripSymbols.length - 1,
      this._landIndex + gameConfig.rowCount + 1,
    );
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const sym = this._stripSymbols[i];
      const inWindow = i >= lo && i <= hi;
      if (!inWindow) {
        sym.setActive(false);
        continue;
      }
      sym.setActive(true);
      sym.clearWinArtifacts();
      sym.clearSpinPresentation();
      sym.setWinPresentation('normal');
    }
    this._spinActiveWindow = null;
  }

  /**
   * Land-pop bursts attach Graphics to the strip; kill them before the next spin
   * so orphaned GSAP tweens cannot keep ticking after a quick stop.
   */
  clearStripParticles() {
    const kids = this.stripContainer.children.slice();
    for (let i = 0; i < kids.length; i++) {
      const child = kids[i];
      if (!(child instanceof Graphics)) continue;
      gsap.killTweensOf(child);
      this.stripContainer.removeChild(child);
      try {
        child.destroy();
      } catch (_e) {
        /* ignore */
      }
    }
  }

  /**
   * Reset win/spin presentation: always idle the visible window (+ pad), flags-only elsewhere.
   * @see {Symbol#prepareForNewRound}
   */
  resetForNewRound() {
    this._clearFastStopTween();
    this._spinLandTail = null;
    this._spinSettleFn = null;
    this._liteScrollBlur = true;
    this.clearStripParticles();
    this._clearScrollBlur();
    const lo = Math.max(0, this._landIndex - 1);
    const hi = Math.min(
      this._stripSymbols.length - 1,
      this._landIndex + gameConfig.rowCount + 1,
    );
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const sym = this._stripSymbols[i];
      const inVisiblePad = i >= lo && i <= hi;
      if (inVisiblePad) {
        gsap.killTweensOf(sym.root);
        sym.setActive(true);
        sym.prepareForNewRound(true);
      } else if (sym._gridPresentation && sym._gridPresentation !== 'normal') {
        sym.clearWinArtifacts();
      }
    }
    this._spinActiveWindow = null;
  }

  /** @deprecated Use {@link #resetForNewRound} */
  resetAllSymbolVisuals() {
    this.resetForNewRound();
  }

  getLandIndex() {
    return this._landIndex;
  }

  getReelIndex() {
    return this.index;
  }
}
